import { useContext, useEffect, useState } from 'react'
import { withRouter } from 'react-router-dom'
import * as H from 'history'
import { useUploadResultPdfListApi } from 'api/sample'
import { useEffectSkipFirst, useForm, useQuery } from 'utils/hooks'
import { Button } from 'react-bootstrap'
import { GlobalStateContext } from 'contexts/global_state_context'
import { Flex } from 'components/shared/flex'
import './preview.scss'
import { setPageSize } from 'utils/util'
import { FaArrowUp } from 'react-icons/fa'
import { CheckPdfResult } from '@craif/product.pdf-result'
import { CheckSample, CheckSampleSearchForm } from 'entities/check_sample'
import { useFetchCheckSampleApi, useFetchCheckSamplesApi } from 'api/check_sample'

const isHomeTest = !!process.env.REACT_APP_IS_HOME_TEST

type Props = {
    history: H.History
}

const CheckResultDetailPage = (props: Props) => {
    const globalState = useContext(GlobalStateContext)
    const params = useQuery<CheckSampleSearchForm>()
    const searchForm = useForm<CheckSampleSearchForm>({
        ...params,
        perPage: 1000,
    })
    const samplesApi = useFetchCheckSamplesApi(searchForm)
    const sampleApi = useFetchCheckSampleApi()
    const [selectedSample, setSelectedSample] = useState<Partial<CheckSample>>({})
    const uploadApi = useUploadResultPdfListApi()

    const changeAddress = (zipCodeText: string, addressText: string, buildingText: string, institutionName: string) => {
        // XPathを使用して要素を取得
        const zipCodeXPath = "//*[@id='top']/div[2]/div[1]/div/div[1]/div[1]/div[1]";
        const addressXPath = "//*[@id='top']/div[2]/div[1]/div/div[1]/div[1]/div[2]";
        const addressBuildingXPath = "//*[@id='top']/div[2]/div[1]/div/div[1]/div[1]/div[3]";
        const institutionNameXPath = "//*[@id='top']/div[2]/div[1]/div/div[1]/div[1]/div[4]";

        const zipCodeElement = document.evaluate(zipCodeXPath, document, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue as HTMLElement;
        const addressElement = document.evaluate(addressXPath, document, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue as HTMLElement;
        const addressBuildingElement = document.evaluate(addressBuildingXPath, document, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue as HTMLElement;
        const institutionNameElement = document.evaluate(institutionNameXPath, document, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue as HTMLElement;

        if (zipCodeElement && addressElement && addressBuildingElement) {
            zipCodeElement.textContent = zipCodeText;
            addressElement.textContent = addressText;
            addressBuildingElement.textContent = buildingText;
            institutionNameElement.textContent = `${institutionName} 御中`;
        }
    }

    const changeCss = () => {
        const leftElementXPath = "//*[@id='top']/div[2]/div[1]/div/div[1]"; // 「住所、バーコードが書かれている」要素のXPath
        const rightElementXPath = "//*[@id='top']/div[2]/div[1]/div/div[2]"; // 「検査結果が書かれている」要素のXPath
        const leftElement = document.evaluate(leftElementXPath, document, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue as HTMLElement;
        const rightElement = document.evaluate(rightElementXPath, document, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue as HTMLElement;
        
        if (leftElement && rightElement) {
            leftElement.style.left = "1.8cm";
            leftElement.style.top = "2cm";
            rightElement.style.right = "0.9cm";
        }
    }

    useEffect(() => {
        if (sampleApi.isSuccess()) {
            const institution = sampleApi?.response?.institution
            const institutionAddress = institution?.address
            if (!isHomeTest && institution && institutionAddress) {
                /**
                 * NOTE: bitの仕様変更で問題が発生し、更新ができなくなった
                 * そのため、以下のように無理やりbitのコンポーネントのcssやcontentを変更している
                 */
                changeAddress(`〒${institutionAddress.zipCode}`, `${institutionAddress.prefecture} ${institutionAddress.city} ${institutionAddress.line}`, institutionAddress.building!, institution.name!)
            }
            changeCss()
        }
    }, [sampleApi.loading])

    useEffect(() => {
        document.title = selectedSample.code!
        if (selectedSample && selectedSample.id) {
            sampleApi.execute(selectedSample.id!)
        }
    }, [selectedSample])

    useEffectSkipFirst(() => {
        globalState.setLoading(uploadApi.loading)
    }, [uploadApi.loading])

    useEffectSkipFirst(() => {
        globalState.setLoading(samplesApi.loading)
        if (samplesApi.isSuccess()) {
            if (samplesApi.response.results.length === 1) {
                setSelectedSample(samplesApi.response.results[0] as CheckSample)
            }
        }
    }, [samplesApi.loading])

    useEffect(() => {
        samplesApi.execute()
        setPageSize('portrait')
    }, [])

    return (
        <div id="top">
            <div className="no-print" style={{ position: 'sticky', top: -20, left: -20, backgroundColor: 'white', padding: 8, zIndex: 1000 }}>
                {selectedSample.id && (
                    <Flex style={{ marginLeft: 20 }} alignItems="flex-end">
                        <div style={{ padding: 15 }}>
                            <Button variant="secondary" size="sm" onClick={() => props.history.push('/check/results')}>
                                戻る
                            </Button>
                        </div>
                        <div style={{ padding: 15 }}>
                            <Button
                                style={{ width: 200 }}
                                onClick={() => {
                                    window.print()
                                }}
                            >
                                印刷プレビュー
                            </Button>
                        </div>
                    </Flex>
                )}
                <Flex style={{ margin: 20, gap: 10 }} flexWrap="wrap">
                    {samplesApi.response.results.map((sample) => {
                        return (
                            <div
                                id={`sample-${sample.id}`}
                                style={
                                    sample.id === selectedSample.id
                                        ? { fontSize: 18, color: 'black', textDecoration: 'none' }
                                        : { fontSize: 14, color: 'blue', textDecoration: 'underline', cursor: 'pointer' }
                                }
                                key={'selectable-sample-' + sample.id}
                                onClick={() => {
                                    setSelectedSample(sample as CheckSample)
                                }}
                            >
                                {sample.code}
                            </div>
                        )
                    })}
                </Flex>
            </div>
            <a
                href="#top"
                className="no-print"
                style={{
                    zIndex: 1000,
                    position: 'fixed',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    top: 30,
                    left: 245,
                    width: 50,
                    height: 50,
                    borderRadius: 50,
                    backgroundColor: '#888',
                }}
            >
                <FaArrowUp color="white"></FaArrowUp>
            </a>
            <div style={{ fontFamily: 'Noto Sans JP' }}>
                {sampleApi.response.id && <CheckPdfResult checkSample={sampleApi.response as any} />}
            </div>
        </div>
    )
}

export default withRouter(CheckResultDetailPage)
